import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mediaList, sortByPopular, categoryLabel } from "@/lib/media";

const FEATURED = sortByPopular(mediaList).slice(0, 4);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % FEATURED.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  const go = (next: number) => {
    setIndex((next + FEATURED.length) % FEATURED.length);
  };

  const slide = FEATURED[index];

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {FEATURED.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl w-full">
          <h1 key={slide.id} className="animate-carousel-fade">
            <span className="block text-primary-200 text-xs md:text-sm font-label tracking-[0.3em] mb-3">
              蜜桃成熟 · 今日焦点
            </span>
            <span className="block text-3xl md:text-5xl font-heading font-semibold text-background-50 leading-tight">
              {slide.title}
            </span>
          </h1>
          <div className="flex items-center flex-wrap gap-2 md:gap-3 mt-4 text-background-50/90 text-xs md:text-sm">
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50">
              {categoryLabel(slide.type)}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center text-accent-300">
                <i className="ri-star-fill"></i>
              </span>
              {slide.rating.toFixed(1)}
            </span>
            <span>{slide.year}</span>
            <span>{slide.region}</span>
            <span>{slide.genres.join(" / ")}</span>
          </div>
          <p className="hidden sm:block text-background-100/80 text-sm md:text-base leading-relaxed mt-4 max-w-xl line-clamp-2">
            {slide.synopsis}
          </p>
          <div className="flex items-center gap-3 mt-6">
            <Link
              to={`/detail/${slide.id}`}
              className="px-5 md:px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors"
            >
              查看详情
            </Link>
            <Link
              to={slide.type === "tv" ? "/category/tv" : "/category/movie"}
              className="px-5 md:px-6 py-2.5 rounded-md border border-background-50/40 text-background-50 hover:bg-background-50/10 text-sm whitespace-nowrap transition-colors"
            >
              更多{categoryLabel(slide.type)}
            </Link>
          </div>

          <div className="flex items-center gap-2 mt-6">
            {FEATURED.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-7 bg-primary-400" : "w-3 bg-background-50/50 hover:bg-background-50/80"
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-foreground-950/70 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-foreground-950/70 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}